import React, { useEffect, useState } from "react";
import { FetchDonationDetails_SPByYearAndMonth } from "../../../Api/Donation";
import { TbFilterX } from "react-icons/tb";
import { VscFilter } from "react-icons/vsc";
import toast from "react-hot-toast";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";

interface DonationAnalyticsProps {
    isDarkMode: boolean;
    url: string;
    readyear: string;
    donationtypes: any[];
    donationpurposes: any[];
}
const account = [
    {
        Type: "Bank",
    },
    {
        Type: "User",
    },
];

const TransactionType = [
    {
        type: 1,
        value: "Ezpay",
    },
    {
        type: 2,
        value: "Bank",
    },
    {
        type: 3,
        value: "Cash",
    },
    {
        type: 4,
        value: "Box",
    },
];

export const DonationAnalytics = ({
    isDarkMode,
    url,
    readyear,
    donationtypes,
    donationpurposes,
}: DonationAnalyticsProps) => {
    const [filterModal, setFilterModal] = useState<boolean>(false);
    const [search, setSearch] = useState<string>("");
    const [years, setYears] = useState<string[]>([]);
    const [months, setMonths] = useState<string[]>([]);
    const [selectYear, setSelectYear] = useState<number | null>(null);
    const [selectMonth, setSelectMonth] = useState<number | null>(null);
    const [donationAnalyticsData, setDonationAnalyticsData] = useState([]);
    const [donationtype, setdonationtype] = useState<string>("");
    const [donationpurpose, setdonationpurpose] = useState<string>("");
    const [accountType, setAccountType] = useState<string>("");
    const [subAccount, setSubAccount] = useState<string>("");
    const [filteredDonations, setFilteredDonations] = useState<any[]>([]);
    const [currentDate, setCurrentDate] = useState<string>("");
    const [loading, setloading] = useState<boolean>(false);

    const [groupedDonationsByType, setGroupedDonationsByType] = useState<any>({});
    const [groupedDonationsByPurpose, setGroupedDonationsByPurpose] =
        useState<any>({});
    const [groupedDonationsByAccount, setGroupedDonationsByAccount] =
        useState<any>({});

    useEffect(() => {
        const startYear = parseInt(readyear);
        const currentYear = new Date().getFullYear();
        const generatedYears = Array.from(
            { length: currentYear - startYear + 1 },
            (_, i) => (startYear + i).toString()
        );
        const generatedMonths = Array.from({ length: 12 }, (_, i) =>
            (i + 1).toString()
        );

        setYears(generatedYears);
        setMonths(generatedMonths);
    }, [readyear]);

    useEffect(() => {
        if (selectYear && selectMonth) {
            handleSearchFunction(selectYear, selectMonth);
        }
    }, [selectYear, selectMonth]);

    const handleSearchFunction = async (year: number, month: number) => {

        setloading(true);
        try {
            const response = await FetchDonationDetails_SPByYearAndMonth(
                url,
                year,
                month
            );
            setDonationAnalyticsData(response);
            setFilteredDonations(response);
            groupData(response);
            toast.success(`Balance details fetch successfully for ${currentDate}`)
        } catch (error) {
            toast.error(`Unable to fetch balance details ${currentDate}`);
        }
        setloading(false);
    };

    useEffect(() => {
        const currentDate = new Date();
        const currentMonth = currentDate.getMonth() + 1;
        const currentYear = currentDate.getFullYear();

        setSelectMonth(currentMonth);
        setSelectYear(currentYear);

        handleSearchFunction(currentYear, currentMonth);
    }, []);

    const groupData = (data: any[]) => {
        const groupedByParent = {};
        const groupedByPurpose = {};
        const groupedByAccount = {};

        data.forEach(({ donationTypeParent, donationType, donationPurpose, account, sub_Account, month, year, donationAmount, expenseAmount, balanceTransferAmount }) => {
            // Group by Donation Type Parent and Type
            if (!groupedByParent[donationTypeParent]) {
                groupedByParent[donationTypeParent] = {
                    parent: donationTypeParent,
                    totalDonationAmount: 0,
                    totalExpenseAmount: 0,
                    totalBalanceTransfer: 0,
                    availableBalance: 0,
                    children: [],
                };
            }

            groupedByParent[donationTypeParent].totalDonationAmount += donationAmount;
            groupedByParent[donationTypeParent].totalExpenseAmount += expenseAmount;
            groupedByParent[donationTypeParent].totalBalanceTransfer += balanceTransferAmount;
            groupedByParent[donationTypeParent].availableBalance =
                groupedByParent[donationTypeParent].totalDonationAmount -
                (groupedByParent[donationTypeParent].totalExpenseAmount +
                    groupedByParent[donationTypeParent].totalBalanceTransfer);

            const existingChild = groupedByParent[donationTypeParent].children.find((child) => child.donationType === donationType);
            if (existingChild) {
                existingChild.totalDonationAmount += donationAmount;
                existingChild.totalExpenseAmount += expenseAmount;
                existingChild.totalBalanceTransfer += balanceTransferAmount;
                existingChild.availableBalance =
                    existingChild.totalDonationAmount -
                    (existingChild.totalExpenseAmount + existingChild.totalBalanceTransfer);
            } else {
                groupedByParent[donationTypeParent].children.push({
                    donationType,
                    totalDonationAmount: donationAmount,
                    totalExpenseAmount: expenseAmount,
                    totalBalanceTransfer: balanceTransferAmount,
                    availableBalance: donationAmount - (expenseAmount + balanceTransferAmount),
                });
            }

            // Group by Donation Purpose (Keep existing logic)
            if (donationPurpose) {
                const key = `${donationPurpose}-${month}-${year}`;
                if (!groupedByPurpose[key]) {
                    groupedByPurpose[key] = {
                        donationPurpose,
                        month,
                        year,
                        totalDonationAmount: 0,
                        totalExpenseAmount: 0,
                        totalBalanceTransfer: 0,
                        availableBalance: 0,
                    };
                }
                groupedByPurpose[key].totalDonationAmount += donationAmount;
                groupedByPurpose[key].totalExpenseAmount += expenseAmount;
                groupedByPurpose[key].totalBalanceTransfer += balanceTransferAmount;
                groupedByPurpose[key].availableBalance =
                    groupedByPurpose[key].totalDonationAmount -
                    (groupedByPurpose[key].totalExpenseAmount +
                        groupedByPurpose[key].totalBalanceTransfer);
            }

            // Group by Account (Keep existing logic)
            if (account) {
                const key = `${account}-${sub_Account}-${month}-${year}`;
                if (!groupedByAccount[key]) {
                    groupedByAccount[key] = {
                        account,
                        sub_Account,
                        month,
                        year,
                        totalDonationAmount: 0,
                        totalExpenseAmount: 0,
                        totalBalanceTransfer: 0,
                        availableBalance: 0,
                    };
                }
                groupedByAccount[key].totalDonationAmount += donationAmount;
                groupedByAccount[key].totalExpenseAmount += expenseAmount;
                groupedByAccount[key].totalBalanceTransfer += balanceTransferAmount;
                groupedByAccount[key].availableBalance =
                    groupedByAccount[key].totalDonationAmount -
                    (groupedByAccount[key].totalExpenseAmount +
                        groupedByAccount[key].totalBalanceTransfer);
            }
        });

        setGroupedDonationsByType(Object.values(groupedByParent)); // DonationTypeParent → DonationType
        setGroupedDonationsByPurpose(Object.values(groupedByPurpose)); // Donation Purpose
        setGroupedDonationsByAccount(Object.values(groupedByAccount)); // Account
    };


    const handleApplyFilters = () => {
        let filteredData = donationAnalyticsData;

        if (donationtype) {
            filteredData = filteredData.filter(
                (donation) => donation.donationType === donationtype
            );
        }
        if (donationpurpose) {
            filteredData = filteredData.filter(
                (donation) => donation.donationPurpose === donationpurpose
            );
        }
        if (accountType) {
            filteredData = filteredData.filter(
                (donation) => donation.account === accountType
            );
        }
        if (donationtype || donationpurpose || accountType || subAccount) {
            setFilteredDonations(filteredData);
            console.log(filteredData);
            groupData(filteredData);
        } else {
            groupData(donationAnalyticsData);
        }
    };

    const setDonationSearchModal = (isOpen: boolean) => {
        setFilterModal(isOpen);
    };

    const handleClear = () => {
        setSearch("");
        setSelectMonth(null);
        setSelectYear(null);
        setdonationtype("");
        setdonationpurpose("");
    };

    const getCurrentDate = () => {
        const date = new Date();
        const year = selectYear !== null ? selectYear : date.getFullYear();
        const month = selectMonth !== null ? selectMonth : date.getMonth() + 1; // getMonth() returns 0-based month
        return `${new Date(year, month - 1).toLocaleString("default", {
            month: "long",
        })} ${year}`;
    };

    // Update the current date whenever selectYear or selectMonth changes
    useEffect(() => {
        setCurrentDate(getCurrentDate());
    }, [selectYear, selectMonth]);

    const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

    const toggleCategory = (category: string) => {
        setExpandedCategory(expandedCategory === category ? null : category);
    }
    console.log(donationtypes);
    console.log(donationpurposes);
    return (
        <div>

            <div
                className="flex gap-1  justify-end items-end px-1 py-2   hover:bg-gray-100 rounded-md cursor-pointer"
                onClick={() => setFilterModal(true)}
            >
                <VscFilter className="text-blue-500 h-7 w-7 " />
                <span className="text-lg font-normal text-gray-600">Filter</span>
            </div>

            <div>
                {loading ? (
                    <div className="flex justify-center items-center h-screen">
                        <div
                            className="flex justify-center items-center h-12 w-12 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-0.125em] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] text-blue-600"
                            role="status"
                        >
                            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
                                Loading...
                            </span>
                        </div>
                    </div>
                ) : <>


                    <div>
                        <div className="overflow-hidden border border-gray-200 dark:border-gray-700 md:rounded-lg mt-5 mb-4">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-200 dark:bg-gray-800 ">
                                    <tr>
                                        <th
                                            scope="col"
                                            rowSpan={2}
                                            className="px-4 py-3.5 text-md font-normal border border-gray-300 dark:border-gray-700 text-left rtl:text-right text-gray-500 dark:text-gray-400"
                                        >
                                            Purpose
                                        </th>
                                        <th
                                            scope="col"
                                            colSpan={3}
                                            className={`px-4 py-3.5 text-md font-normal bg-gray-300 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-center rtl:text-right ${isDarkMode ? " text-gray-500" : " text-gray-500"
                                                }`}
                                        >
                                            <span>{currentDate}</span>
                                        </th>
                                    </tr>
                                    <tr className="border border-gray-300 dark:border-gray-700">
                                        <th
                                            scope="col"

                                            className={`px-4 py-3.5 text-md font-normal text-left rtl:text-right ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                }`}
                                        >
                                            <span>

                                                Total Donations
                                            </span>
                                        </th>

                                        <th
                                            scope="col"
                                            className={`px-4 py-3.5 text-md font-normal text-left rtl:text-right ${isDarkMode ? " text-gray-500" : " text-gray-500"
                                                }`}
                                        >
                                            <span>Expenses</span>
                                        </th>
                                        <th
                                            scope="col"
                                            className={`px-4 py-3.5 text-md font-normal text-left rtl:text-right ${isDarkMode ? " text-gray-500" : " text-gray-500"
                                                }`}
                                        >
                                            Balance
                                        </th>
                                    </tr>
                                </thead>

                                <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                                    {Object.keys(groupedDonationsByPurpose).length > 0
                                        ? Object.values(groupedDonationsByPurpose).map(
                                            (donation: any, index: number) => (
                                                <tr key={index}>
                                                    <td
                                                        className={`px-4 py-4 text-sm font-medium ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.donationPurpose}
                                                    </td>
                                                    <td
                                                        className={`px-4 py-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.totalDonationAmount}
                                                    </td>
                                                    <td
                                                        className={`px-4 py-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.totalExpenseAmount}
                                                    </td>
                                                    <td
                                                        className={`px-4 py-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.availableBalance}
                                                    </td>
                                                </tr>
                                            )
                                        )
                                        : null}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="overflow-hidden border border-gray-200 dark:border-gray-700 md:rounded-lg mt-5 mb-4">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                            <thead className="bg-gray-200 dark:bg-gray-800">
                                <tr>
                                    <th scope="col" rowSpan={2} className="px-4 py-3.5 text-md font-normal border border-gray-300 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                                        Type
                                    </th>
                                    <th scope="col" colSpan={4} className="px-4 py-3.5 text-md font-normal bg-gray-300 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-center text-gray-500">
                                        <span>{currentDate}</span>
                                    </th>
                                </tr>
                                <tr className="border border-gray-300 dark:border-gray-700">
                                    <th scope="col" className="px-4 py-3.5 text-md font-normal text-left text-gray-500">Total Donations</th>
                                    <th scope="col" className="px-4 py-3.5 text-md font-normal text-left text-gray-500">Expenses</th>
                                    <th scope="col" className="px-4 py-3.5 text-md font-normal text-left text-gray-500">Balance</th>
                                    <th scope="col" className="px-4 py-3.5 text-md font-normal text-left text-gray-500">Action</th>
                                </tr>
                            </thead>

                            <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                                {groupedDonationsByType.length > 0 ? groupedDonationsByType.map((parentData) => (
                                    <React.Fragment key={parentData.parent}>
                                        <tr
                                            onClick={() => toggleCategory(parentData.parent)}
                                            className="cursor-pointer bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-700"
                                        >
                                            <td className="px-4 py-4 text-md textce font-semibold text-gray-700 dark:text-gray-300">{parentData.parent}</td>
                                            <td className="px-4 py-4 text-sm font-semibold text-gray-700 dark:text-gray-300 text-left">
                                                {parentData.totalDonationAmount}
                                            </td>
                                            <td className=" px-4 py-4 text-sm font-semibold text-gray-700 dark:text-gray-300 text-lrft">
                                                {parentData.totalExpenseAmount}
                                            </td>
                                            <td className="px-4 py-4 text-sm font-semibold text-gray-700 dark:text-gray-300 text-lrft">
                                                {parentData.availableBalance}
                                            </td>
                                            <td className="px-4 py-4 text-sm font-semibold text-gray-700 dark:text-gray-300 text-left">
                                                {expandedCategory === parentData.parent ? <FaChevronUp /> : <FaChevronDown />}
                                            </td>
                                        </tr>

                                        {/* Child Rows for Donation Types */}
                                        {expandedCategory === parentData.parent &&
                                            parentData.children.map((child, index) => (
                                                <tr key={`${parentData.parent}-${index}`} className="border border-gray-300 dark:border-gray-700">
                                                    <td className="px-4 py-4 text-sm font-medium text-gray-500">{child.donationType}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-500">{child.totalDonationAmount}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-500">{child.totalExpenseAmount}</td>
                                                    <td className="px-4 py-4 text-sm text-gray-500">{child.availableBalance}</td>
                                                </tr>
                                            ))}
                                    </React.Fragment>
                                )) : null}
                            </tbody>
                        </table>
                    </div>


                    <div>
                        <div className=" overflow-hidden border border-gray-200 dark:border-gray-700 md:rounded-lg mt-5 mb-4">
                            <table className=" min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                <thead className="bg-gray-200 dark:bg-gray-800 ">
                                    <tr>
                                        <th
                                            scope="col"
                                            rowSpan={2}
                                            className="px-4 py-3.5 text-md font-normal border border-gray-300 dark:border-gray-700 text-left rtl:text-right text-gray-500 dark:text-gray-400"
                                        >
                                            Account
                                        </th>
                                        <th
                                            scope="col"
                                            colSpan={4}
                                            className={`px-4 py-3.5 text-md font-normal bg-gray-300 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 text-center rtl:text-right ${isDarkMode ? " text-gray-500" : " text-gray-500"
                                                }`}
                                        >
                                            <span>{currentDate}</span>
                                        </th>
                                    </tr>
                                    <tr className="border border-gray-300 dark:border-gray-700">
                                        <th
                                            scope="col"

                                            className={`px-4 py-3.5 text-md font-normal text-left rtl:text-right ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                }`}
                                        >
                                            <span>

                                                Total Donations
                                            </span>
                                        </th>

                                        <th
                                            scope="col"
                                            className={`px-4 py-3.5 text-md font-normal text-left rtl:text-right ${isDarkMode ? " text-gray-500" : " text-gray-500"
                                                }`}
                                        >
                                            <span>Expenses</span>
                                        </th>
                                        <th
                                            scope="col"
                                            className={`px-4 py-3.5 text-md font-normal text-left rtl:text-right ${isDarkMode ? " text-gray-500" : " text-gray-500"
                                                }`}
                                        >
                                            Balance Transffered
                                        </th>
                                        <th
                                            scope="col"
                                            className={`px-4 py-3.5 text-md font-normal text-left rtl:text-right ${isDarkMode ? " text-gray-500" : " text-gray-500"
                                                }`}
                                        >
                                            Balance
                                        </th>
                                    </tr>
                                </thead>

                                <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700 dark:bg-gray-900">
                                    {Object.keys(groupedDonationsByAccount).length > 0
                                        ? Object.values(groupedDonationsByAccount).map(
                                            (donation: any, index: number) => (
                                                <tr key={index}>
                                                    <td
                                                        className={`px-4 py-4 text-sm font-medium ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.account}
                                                    </td>
                                                    <td
                                                        className={`px-4 py-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.totalDonationAmount}
                                                    </td>
                                                    <td
                                                        className={`px-4 py-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.totalExpenseAmount}
                                                    </td>
                                                    <td
                                                        className={`px-4 py-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.totalBalanceTransfer}
                                                    </td>
                                                    <td
                                                        className={`px-4 py-4 text-sm ${isDarkMode ? "text-gray-500" : "text-gray-500"
                                                            }`}
                                                    >
                                                        {donation.availableBalance}
                                                    </td>
                                                </tr>
                                            )
                                        )
                                        : null}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div
                        className={`fixed inset-y-0 right-0 z-50 md:w-[40%] w-1/2 bg-[#ffff]  p-6 shadow-xl ${filterModal ? " translate-x-0  " : "translate-x-full"
                            }  duration-300`}
                    >
                        <h2 className=" text-xl font-semibold  text-black mt-4">
                            Donation Analytics Filter
                        </h2>
                        <button
                            onClick={() => setDonationSearchModal(false)}
                            className="absolute top-0 right-0  text-3xl text-gray-400 hover:text-black h-10 w-10"
                        >
                            &times;
                        </button>
                        <div className="  flex items-center gap-4 mt-4">
                            <div
                                onClick={handleClear}
                                className=" cursor-pointer flex items-center gap-2 hover:bg-gray-200 px-1 py-1 rounded-sm"
                            >
                                <TbFilterX className="text-blue-500 h-5 w-5" />
                                <span className="text-sm">Clear all</span>
                            </div>
                        </div>
                        <div className="mt-5">
                            <label className="text-base font-semibold text-gray-900">
                                Month:
                            </label>
                            <select
                                value={selectMonth}
                                onChange={(e) => setSelectMonth(Number(e.target.value))}
                                className="w-[96%] p-1 text-base text-gray-700 bg-[#ffff] border border-gray-800 rounded-sm focus:outline-none focus:border-blue-400"
                            >
                                <option value="">Select month</option>
                                {months.map((month) => (
                                    <option key={month} value={month}>
                                        {month}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mt-5">
                            <label className="text-base font-semibold text-gray-900">Year:</label>
                            <select
                                value={selectYear}
                                onChange={(e) => setSelectYear(Number(e.target.value))}
                                className="w-[96%] p-1 text-base text-gray-700 bg-[#ffff] border border-gray-800 rounded-sm focus:outline-none focus:border-blue-400"
                            >
                                <option value="">Select year</option>
                                {years.map((year) => (
                                    <option key={year} value={year}>
                                        {year}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="mt-5">
                            <label className=" text-sm font-semibold text-gray-900 ">
                                Donation Type :
                            </label>
                            <select
                                id="donationType"
                                required
                                value={donationtype}
                                onChange={(e) => setdonationtype(e.target.value)}
                                className="w-[96%] p-1 text-base text-gray-700 bg-[#ffff] border border-gray-800 rounded-sm  focus:outline-non focus:border-blue-400"
                            >
                                <option value="" disabled>
                                    Select Donation Type
                                </option>
                                {donationtypes.map((option: any) => (
                                    <option key={option.id} value={option.name}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mt-5">
                            <label className=" text-base font-semibold text-gray-900 ">
                                Donation Purpose :
                            </label>
                            <select
                                id="donationType"
                                required
                                value={donationpurpose}
                                onChange={(e) => setdonationpurpose(e.target.value)}
                                className="w-[96%] p-1 text-base text-gray-700 bg-[#ffff] border border-gray-800 rounded-sm  focus:outline-non focus:border-blue-400"
                            >
                                <option value="" disabled>
                                    Select Donation purpose
                                </option>
                                {donationpurposes.map((option: any) => (
                                    <option key={option.id} value={option.name}>
                                        {option.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mt-5">
                            <label className="text-base font-semibold text-gray-900">
                                Account Type:
                            </label>
                            <select
                                value={accountType}
                                onChange={(e) => setAccountType(e.target.value)}
                                className="w-[96%] p-1 text-base text-gray-700 bg-[#ffff] border border-gray-800 rounded-sm focus:outline-none focus:border-blue-400"
                            >
                                <option value="">Select Account</option>
                                {account.map((acc, index) => (
                                    <option key={index} value={acc.Type}>
                                        {acc.Type}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="mt-5">
                            <label className="text-base font-semibold text-gray-900">
                                Sub-Account:
                            </label>
                            <select
                                value={subAccount}
                                onChange={(e) => setSubAccount(e.target.value)}
                                className="w-[96%] p-1 text-base text-gray-700 bg-[#ffff] border border-gray-800 rounded-sm focus:outline-none focus:border-blue-400"
                            >
                                <option value="">Select Transaction Type</option>
                                {TransactionType.map((Ttype: any, index) => (
                                    <option key={index} value={Ttype.value}>
                                        {Ttype.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex gap-2 md:gap-4 mt-5">
                            <button
                                className="mt-2 px-4 py-1 text-white border border-gray-800 rounded-sm bg-blue-500 hover:bg-blue-700"
                                onClick={handleApplyFilters}
                            >
                                Apply
                            </button>
                            <button
                                className="mt-2 px-4 py-1 border border-gray-800 rounded-sm hover:bg-gray-200"
                                onClick={() => setFilterModal(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </>}
            </div>
        </div>
    );
};
